import { useCallback, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CellValue, DayNote, Lesson, Period, PeriodInput, Subject } from '../api';
import { droppedCell, droppedSubject, isCellDrag, isSubjectDrag } from '../drag';
import type { CellRef } from '../drag';
import type { Slot } from '../now';
import { PeriodForm } from '../PeriodForm';
import { useMediaQuery } from '../useMediaQuery';
import { mondayOf } from '../weekMath';
import type { IsoWeek } from '../weekMath';
import { themeStyle } from '../../theme/applyTheme';
import { useTheme } from '../../theme/api';
import { Cell } from './Cell';
import type { CellMove } from './Cell';
import type { LessonDetails } from './CellDetails';
import { DAY_NAMES, DAY_NAMES_LONG } from './dayNames';
import { DayHeader } from './DayHeader';
import { NotesRow } from './NotesRow';
import { PeriodLabel } from './PeriodLabel';
import { UndoBar } from './UndoBar';

export type PlannerSheetProps = {
  periods: Period[];
  subjects: Subject[];
  lessons: Lesson[];
  /** Read-only and large on the board; editable otherwise. */
  mode?: 'edit' | 'board';
  /** A real week: dates in the heads and "Weekplanning 08" in the header; absent on the default timetable. */
  week?: IsoWeek;
  /** Show one day only (the day view). On a narrow screen the sheet picks a day itself. */
  day?: number;
  /** Today and the running period. */
  highlight?: Slot;
  /** When given, the notes row shows. */
  dayNotes?: DayNote[];
  /** Header left: logo and title. */
  title?: ReactNode;
  dayHref?: (weekday: number) => string;
  onCellChange?: (cell: CellRef, value: CellValue) => void | Promise<unknown>;
  onFieldsChange?: (cell: CellRef, values: Record<string, string>) => void;
  onDetailsChange?: (cell: CellRef, details: LessonDetails) => void;
  onDayNoteChange?: (weekday: number, note: string) => void;
  /** Editing the periods of this sheet in place. */
  periodActions?: {
    update: (id: string, input: PeriodInput) => void;
    remove: (id: string) => void;
    create: (input: PeriodInput) => void;
    pending: boolean;
    error: string | null;
  };
  /** Rendered between the header and the grid, e.g. an empty state. */
  children?: ReactNode;
};

const ALL_DAYS = [1, 2, 3, 4, 5];
const DAY_MS = 86_400_000;
const same = (a: CellRef | null, b: CellRef) => a?.periodId === b.periodId && a.weekday === b.weekday;
const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

type Undo = { message: string; changes: { cell: CellRef; before: CellValue }[] };

/**
 * Days × periods as one grid, shared by the week, the day view, the default timetable and the board.
 * Knows which cell is open and where the keyboard goes next; everything else is a callback.
 */
export function PlannerSheet(props: PlannerSheetProps) {
  const { periods, subjects, lessons, mode = 'edit', week, day, highlight, dayNotes, title, dayHref, onCellChange, onFieldsChange, onDetailsChange, onDayNoteChange, periodActions, children } = props;
  const narrow = useMediaQuery('(max-width: 767px)');
  const [pickedDay, setPickedDay] = useState(highlight?.weekday ?? 1);
  const [editing, setEditing] = useState<CellRef | null>(null);
  const [justFilled, setJustFilled] = useState<CellRef | null>(null);
  const [over, setOver] = useState<CellRef | null>(null);
  const [adding, setAdding] = useState(false);
  const [undo, setUndo] = useState<Undo | null>(null);
  const dismissUndo = useCallback(() => setUndo(null), []);
  const planner = useTheme('planner').data;
  const ownTheme = planner !== undefined && !planner.inherited;

  const singleDay = day ?? (narrow && mode === 'edit' ? pickedDay : null);
  const days = singleDay ? [singleDay] : ALL_DAYS;
  const lessonPeriods = periods.filter((period) => !period.isBreak);
  const subjectById = new Map(subjects.map((subject) => [subject.id, subject]));
  const lessonAt = (cell: CellRef) => lessons.find((lesson) => lesson.periodId === cell.periodId && lesson.weekday === cell.weekday);
  const valueAt = (cell: CellRef): CellValue => {
    const lesson = lessonAt(cell);
    return { subjectId: lesson?.subjectId ?? null, note: lesson?.note ?? '' };
  };
  const monday = week ? mondayOf(week) : null;
  const dayNumber = (weekday: number) => (monday ? new Date(monday.getTime() + (weekday - 1) * DAY_MS).getUTCDate() : undefined);

  function save(cell: CellRef, value: CellValue) {
    const before = valueAt(cell);
    if (before.subjectId === value.subjectId && before.note === value.note) return;
    void onCellChange?.(cell, value);
    if (value.subjectId !== null && value.subjectId !== before.subjectId) setJustFilled(cell);
  }

  /** Saves several cells at once and remembers what they were, so one click can put it back. */
  function bulk(message: string, edits: { cell: CellRef; value: CellValue }[]) {
    const changes = edits.flatMap(({ cell, value }) => {
      const before = valueAt(cell);
      return before.subjectId === value.subjectId && before.note === value.note ? [] : [{ cell, before }];
    });
    edits.forEach(({ cell, value }) => save(cell, value));
    setUndo(changes.length > 0 ? { message, changes } : null);
  }

  /** The cell the keyboard moves to: Tab walks down the day and then on to the next day. */
  function neighbour(cell: CellRef, move: CellMove): CellRef | null {
    const row = lessonPeriods.findIndex((period) => period.id === cell.periodId);
    const column = days.indexOf(cell.weekday);
    let nextRow = row;
    let nextColumn = column;
    if (move === 'next' || move === 'previous') {
      const step = move === 'next' ? 1 : -1;
      nextRow = row + step;
      if (nextRow < 0 || nextRow >= lessonPeriods.length) {
        nextRow = step === 1 ? 0 : lessonPeriods.length - 1;
        nextColumn = column + step;
      }
    } else if (move === 'up') nextRow = row - 1;
    else if (move === 'down') nextRow = row + 1;
    else if (move === 'left') nextColumn = column - 1;
    else nextColumn = column + 1;
    const period = lessonPeriods[nextRow];
    const weekday = days[nextColumn];
    return period && weekday ? { periodId: period.id, weekday } : null;
  }

  function commit(cell: CellRef, value: CellValue, move?: CellMove) {
    save(cell, value);
    if (move) setEditing(neighbour(cell, move));
  }

  // Drops: a subject from the palette sets the cell (note stays); a cell on a cell swaps, with Alt it copies.
  function dropProps(cell: CellRef) {
    if (!onCellChange) return {};
    return {
      'data-drop-over': same(over, cell) ? 'true' : undefined,
      onDragOver: (event: DragEvent<HTMLDivElement>) => {
        if (!isSubjectDrag(event) && !isCellDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = isCellDrag(event) && !event.altKey ? 'move' : 'copy';
        if (!same(over, cell)) setOver(cell);
      },
      onDragLeave: (event: DragEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(null);
      },
      onDrop: (event: DragEvent<HTMLDivElement>) => {
        setOver(null);
        if (isSubjectDrag(event)) {
          event.preventDefault();
          const subjectId = droppedSubject(event);
          save(cell, { subjectId, note: subjectId === null ? '' : valueAt(cell).note });
          return;
        }
        if (!isCellDrag(event)) return;
        event.preventDefault();
        const source = droppedCell(event);
        if (!source || same(source, cell)) return;
        const moved = valueAt(source);
        if (event.altKey) return save(cell, moved);
        bulk('Vakjes gewisseld.', [
          { cell, value: moved },
          { cell: source, value: valueAt(cell) },
        ]);
      },
    };
  }

  const copyDay = (from: number, to: number) =>
    bulk(
      `${capitalise(DAY_NAMES_LONG[from - 1] ?? '')} naar ${DAY_NAMES_LONG[to - 1] ?? ''} gekopieerd.`,
      lessonPeriods.map((period) => ({ cell: { periodId: period.id, weekday: to }, value: valueAt({ periodId: period.id, weekday: from }) })),
    );
  const clearDay = (weekday: number) =>
    bulk(
      `${capitalise(DAY_NAMES_LONG[weekday - 1] ?? '')} leeggemaakt.`,
      lessonPeriods.map((period) => ({ cell: { periodId: period.id, weekday }, value: { subjectId: null, note: '' } })),
    );

  const columns = `minmax(6rem, 8rem) repeat(${days.length}, minmax(0, 1fr))`;
  // On the board the lesson rows share the height of the screen; the day heads, breaks and notes keep their own height.
  const boardRows = ['auto', ...periods.map((period) => (period.isBreak ? 'auto' : 'minmax(min-content, 1fr)')), ...(dayNotes ? ['auto'] : [])].join(' ');

  return (
    <div className={`sheet flex min-h-0 flex-1 flex-col ${ownTheme ? 'sheet-own themed' : ''}`} style={ownTheme && planner ? themeStyle(planner) : undefined}>
      {(title || week) && (
        <header
          aria-label="Veltitel"
          className={`flex flex-wrap items-end justify-between gap-3 px-2 pb-2 ${mode === 'board' ? 'pt-2 text-[clamp(1rem,1.6vw,2.4rem)]' : ''} ${planner?.titleOutline ? 'title-outline' : ''}`}
        >
          <div className="flex items-end gap-4">{title}</div>
          {week && (
            <span className="text-[1.6em] font-extrabold leading-none" style={{ fontFamily: 'var(--font-heading)' }}>
              Weekplanning {String(week.week).padStart(2, '0')}
            </span>
          )}
        </header>
      )}
      <section aria-label="Weekplanner" className={`panel sheet-paper flex flex-1 flex-col gap-4 ${mode === 'board' ? 'min-h-0 p-6' : ''}`}>
        {children}
        {narrow && mode === 'edit' && !day && (
          <Tabs
            value={String(pickedDay)}
            onValueChange={(value) => {
              setPickedDay(Number(value));
              setEditing(null);
            }}
          >
            <TabsList aria-label="Dag kiezen" className="grid w-full grid-cols-5">
              {ALL_DAYS.map((weekday) => (
                <TabsTrigger key={weekday} value={String(weekday)} className="min-h-11 flex-col" aria-current={highlight?.weekday === weekday ? 'date' : undefined}>
                  {dayNumber(weekday) !== undefined && <span className="day-number">{dayNumber(weekday)}</span>}
                  <span>{DAY_NAMES[weekday - 1]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        {periods.length > 0 && (
          <div role="grid" aria-label="Weekrooster" className={`grid gap-1 ${mode === 'board' ? 'board-grid text-[clamp(0.9rem,1.5vw,2.2rem)]' : ''}`} style={{ gridTemplateColumns: columns, ...(mode === 'board' ? { gridTemplateRows: boardRows } : {}) }}>
            <div role="row" className="contents">
              <div role="columnheader" aria-label="Tijd" />
              {days.map((weekday) => (
                <DayHeader
                  key={weekday}
                  weekday={weekday}
                  dayNumber={dayNumber(weekday)}
                  today={highlight?.weekday === weekday}
                  {...(dayHref ? { href: dayHref(weekday) } : {})}
                  {...(onCellChange && mode === 'edit' && !singleDay ? { onCopyTo: (to: number) => copyDay(weekday, to), onClear: () => clearDay(weekday) } : {})}
                />
              ))}
            </div>
            {periods.map((period) => {
              const running = highlight?.periodId === period.id;
              return (
                <div key={period.id} role="row" className="contents">
                  <PeriodLabel
                    period={period}
                    running={running}
                    {...(periodActions && mode === 'edit'
                      ? {
                          onUpdate: (input: PeriodInput) => periodActions.update(period.id, input),
                          onRemove: () => periodActions.remove(period.id),
                          pending: periodActions.pending,
                          error: periodActions.error,
                        }
                      : {})}
                  />
                  {period.isBreak ? (
                    <div role="gridcell" aria-label={`${period.label}, pauze`} className="row-break self-center" style={{ gridColumn: `span ${days.length}` }} />
                  ) : (
                    days.map((weekday) => {
                      const cell = { periodId: period.id, weekday };
                      const lesson = lessonAt(cell);
                      return (
                        <Cell
                          key={weekday}
                          label={`${DAY_NAMES[weekday - 1] ?? ''} ${period.label}`}
                          lesson={lesson}
                          subject={lesson?.subjectId ? subjectById.get(lesson.subjectId) : undefined}
                          subjects={subjects}
                          mode={mode}
                          editing={same(editing, cell)}
                          today={highlight?.weekday === weekday}
                          running={running && highlight?.weekday === weekday && mode === 'board'}
                          justFilled={same(justFilled, cell)}
                          detailsInline={Boolean(day)}
                          {...(onCellChange
                            ? {
                                onOpen: () => setEditing(cell),
                                onCommit: (value: CellValue, move?: CellMove) => commit(cell, value, move),
                                onCancel: () => setEditing(null),
                                dragRef: cell,
                                dropProps: dropProps(cell),
                              }
                            : {})}
                          {...(onFieldsChange ? { onFields: (values: Record<string, string>) => onFieldsChange(cell, values) } : {})}
                          {...(onDetailsChange ? { onDetails: (details: LessonDetails) => onDetailsChange(cell, details) } : {})}
                        />
                      );
                    })
                  )}
                </div>
              );
            })}
            {dayNotes && <NotesRow notes={dayNotes} days={days} mode={mode} {...(onDayNoteChange ? { onChange: onDayNoteChange } : {})} />}
          </div>
        )}
        {undo && mode === 'edit' && (
          <UndoBar
            message={undo.message}
            onDismiss={dismissUndo}
            onUndo={() => {
              // Straight to the callback: `save` compares with props that may not have caught up with the change yet.
              undo.changes.forEach(({ cell, before }) => void onCellChange?.(cell, before));
              setUndo(null);
            }}
          />
        )}
        {periodActions && mode === 'edit' && (
          <Popover open={adding} onOpenChange={setAdding}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="no-print min-h-11 self-start">
                <Plus /> Voeg tijdvak toe
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80">
              <p className="mb-2 text-sm opacity-70">Alleen deze week; het rooster blijft zoals het is.</p>
              <PeriodForm
                submitLabel="Toevoegen"
                pending={periodActions.pending}
                onSubmit={(input) => {
                  periodActions.create(input);
                  setAdding(false);
                }}
                onCancel={() => setAdding(false)}
              />
              {periodActions.error && (
                <p role="alert" className="mt-2 text-sm">
                  {periodActions.error}
                </p>
              )}
            </PopoverContent>
          </Popover>
        )}
      </section>
    </div>
  );
}
